import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { OpenResponseExercise } from '../../../student/mockLessonContent';
import { BLUE, cardSx, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, RING_TRACK, SLATE, SURFACE_SOFT, blueButtonSx, yellowButtonSx } from '../../../student/studentStyles';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';
import OpenResponseFeedbackPanel from './OpenResponseFeedbackPanel';

interface Props {
    exercise: OpenResponseExercise;
    onReady: ExerciseReady;
}

function elapsedLabel(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

interface SubmissionSnapshot {
    answer: string;
    takeUrl: string | null;
}

export default function OpenResponseExerciseCard({ exercise, onReady }: Props) {
    const desktop = useExerciseLayout() === 'desktop';
    const needsText = exercise.kind !== 'openAudio';
    const needsAudio = exercise.kind !== 'openText';
    const [answer, setAnswer] = React.useState('');
    const [starting, setStarting] = React.useState(false);
    const [recording, setRecording] = React.useState(false);
    const [recordingSeconds, setRecordingSeconds] = React.useState(0);
    const [take, setTake] = React.useState<{ url: string; seconds: number } | null>(null);
    const [error, setError] = React.useState('');
    const [playing, setPlaying] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [lastFeedbackSubmission, setLastFeedbackSubmission] = React.useState<SubmissionSnapshot | null>(null);
    const [feedbackRound, setFeedbackRound] = React.useState(0);
    const recorderRef = React.useRef<MediaRecorder | null>(null);
    const playbackRef = React.useRef<HTMLAudioElement | null>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const timerRef = React.useRef<number>();
    const secondsRef = React.useRef(0);
    const takeUrlRef = React.useRef<string | null>(null);
    const mountedRef = React.useRef(true);
    const requestPendingRef = React.useRef(false);
    const submittedRef = React.useRef(false);
    const lastFeedbackSubmissionRef = React.useRef<SubmissionSnapshot | null>(null);
    const feedbackRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (feedbackRound > 0) feedbackRef.current?.focus();
    }, [feedbackRound]);

    // From the feedback, jump to where the student revises: the writing, or the recorder.
    const reviseAnswer = () => {
        const target = document.getElementById(needsText ? `${exercise.id}-answer` : `${exercise.id}-recording`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.focus({ preventScroll: true });
    };

    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            window.clearInterval(timerRef.current);
            // The audio element mounts after this effect runs, so the latest one is wanted here.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            playbackRef.current?.pause();
            if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
            streamRef.current?.getTracks().forEach(track => track.stop());
            if (takeUrlRef.current) URL.revokeObjectURL(takeUrlRef.current);
        };
    }, []);

    const clearTake = () => {
        playbackRef.current?.pause();
        setPlaying(false);
        if (takeUrlRef.current) URL.revokeObjectURL(takeUrlRef.current);
        takeUrlRef.current = null;
        setTake(null);
    };

    const startRecording = async () => {
        if (submittedRef.current || requestPendingRef.current || recorderRef.current?.state === 'recording') return;
        setError('');
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setError('Recording is unavailable in this browser. Try a current browser on localhost or a secure connection.');
            return;
        }
        requestPendingRef.current = true;
        setStarting(true);
        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!mountedRef.current || submittedRef.current) { stream.getTracks().forEach(track => track.stop()); return; }
            const activeStream = stream;
            const recorder = new MediaRecorder(activeStream);
            clearTake();
            streamRef.current = activeStream;
            recorderRef.current = recorder;
            const chunks: BlobPart[] = [];
            recorder.ondataavailable = event => { if (event.data.size > 0) chunks.push(event.data); };
            recorder.onstop = () => {
                activeStream.getTracks().forEach(track => track.stop());
                streamRef.current = null;
                window.clearInterval(timerRef.current);
                if (!mountedRef.current || submittedRef.current) return;
                setRecording(false);
                if (chunks.length === 0) { setError('No audio was saved. Please record again.'); return; }
                const url = URL.createObjectURL(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
                takeUrlRef.current = url;
                setTake({ url, seconds: Math.max(1, secondsRef.current) });
            };
            recorder.start();
            secondsRef.current = 0;
            setRecordingSeconds(0);
            setRecording(true);
            timerRef.current = window.setInterval(() => {
                secondsRef.current += 1;
                setRecordingSeconds(secondsRef.current);
            }, 1000);
        } catch {
            stream?.getTracks().forEach(track => track.stop());
            setError('Microphone access was not available. Check your browser permission and try again.');
        } finally {
            requestPendingRef.current = false;
            if (mountedRef.current) setStarting(false);
        }
    };

    const stopRecording = () => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    };

    const changeAnswer = (value: string) => {
        if (takeUrlRef.current) clearTake();
        setAnswer(value);
    };

    const togglePlayback = async () => {
        const player = playbackRef.current;
        if (!player) return;
        if (!player.paused) {
            player.pause();
            setPlaying(false);
            return;
        }
        try {
            await player.play();
            setPlaying(true);
            setError('');
        } catch {
            setError('This recording could not be played. Record another take to try again.');
        }
    };

    const canSubmit = (!needsText || answer.trim().length > 0) && (!needsAudio || take != null);
    const hasRevision = !lastFeedbackSubmission || answer !== lastFeedbackSubmission.answer || (take?.url ?? null) !== lastFeedbackSubmission.takeUrl;
    const submitAnswer = () => {
        if (submittedRef.current || requestPendingRef.current || recording || recorderRef.current?.state === 'recording' || !canSubmit) return;
        const previous = lastFeedbackSubmissionRef.current;
        if (exercise.revisionFeedback) {
            if (previous && answer === previous.answer && (take?.url ?? null) === previous.takeUrl) return;
            const snapshot = { answer, takeUrl: take?.url ?? null };
            lastFeedbackSubmissionRef.current = snapshot;
            setLastFeedbackSubmission(snapshot);
            setFeedbackRound(round => round + 1);
            return;
        }
        submittedRef.current = true;
        setSubmitted(true);
        onReady('done');
    };
    const finishAnswer = () => {
        if (!exercise.revisionFeedback || submittedRef.current || requestPendingRef.current || recording || recorderRef.current?.state === 'recording' || !canSubmit) return;
        submittedRef.current = true;
        setSubmitted(true);
        onReady('done');
    };
    const responseLabel = exercise.kind === 'openTextAudio' ? 'WRITE & SPEAK' : exercise.kind === 'openAudio' ? 'SPEAK' : 'WRITE';
    const criteriaGroups = [
        { role: 'required' as const, heading: 'Include in your answer', Icon: CheckCircleOutlineRoundedIcon },
        { role: 'suggested' as const, heading: 'Ideas to help you', Icon: LightbulbOutlinedIcon },
        { role: 'example' as const, heading: 'Example only', Icon: InfoOutlinedIcon },
    ];

    return <Box sx={{ ...cardSx, overflow: 'hidden' }}>
        {needsAudio ? <SpeakingHero label={responseLabel} hanzi={exercise.question} /> :
            <Box sx={{ px: desktop ? 3.5 : 2.5, pt: desktop ? 3.5 : 2.5, pb: 2.5, borderBottom: `1px solid ${RING_TRACK}` }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.09em', color: BLUE }}>{responseLabel}</Typography>
                <Typography component="h2" sx={{ mt: 1, fontFamily: POPPINS, fontWeight: 600, fontSize: desktop ? 25 : 21, color: NAVY }}>{exercise.title}</Typography>
            </Box>}

        <Stack spacing={3} sx={{ p: desktop ? 3.5 : 2.5 }}>
            <Typography sx={{ fontFamily: INTER, fontSize: 14, lineHeight: 1.6, color: SLATE }}>
                {exercise.guidance}{exercise.revisionFeedback && ' In this preview, request sample feedback, revise as often as you like, then finish when you are ready.'}
            </Typography>
            {!needsAudio && <Box>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: SLATE }}>YOUR QUESTION</Typography>
                <Typography lang="zh" sx={{ mt: 1, fontFamily: POPPINS, fontWeight: 500, fontSize: desktop ? 23 : 20, lineHeight: 1.55, color: NAVY }}>
                    {exercise.question}
                </Typography>
            </Box>}

            <Box sx={{ borderRadius: '16px', bgcolor: SURFACE_SOFT, p: 2 }}>
                {criteriaGroups.map(({ role, heading, Icon }) => {
                    const items = exercise.criteria.filter(item => item.role === role);
                    if (!items.length) return null;
                    return <Box key={role} sx={{ '& + &': { mt: 2 } }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14, color: NAVY }}>{heading}</Typography>
                        <Stack component="ul" spacing={1} sx={{ m: 0, mt: 1.25, pl: 0, listStyle: 'none' }}>
                            {items.map(item => <Stack component="li" key={item.text} direction="row" spacing={1} alignItems="flex-start">
                                <Icon aria-hidden="true" sx={{ mt: '2px', fontSize: 17, color: role === 'required' ? BLUE : SLATE, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.5, color: NAVY }}>{item.text}</Typography>
                            </Stack>)}
                        </Stack>
                    </Box>;
                })}
                {exercise.speakingHint && <Typography sx={{ mt: 1.5, fontFamily: INTER, fontSize: 12, color: SLATE }}>{exercise.speakingHint}</Typography>}
            </Box>

            {feedbackRound > 0 && exercise.revisionFeedback && <Box ref={feedbackRef} tabIndex={-1}
                role={exercise.feedbackPreview ? 'region' : 'status'}
                aria-label={exercise.feedbackPreview ? 'Example feedback on a sample answer' : 'Sample feedback'}
                sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${BLUE}40`, bgcolor: `${BLUE}0D`, '&:focus-visible': { outline: `3px solid ${BLUE}`, outlineOffset: 2 } }}>
                {exercise.feedbackPreview ? <OpenResponseFeedbackPanel preview={exercise.feedbackPreview} submitted={submitted} onRevise={reviseAnswer} /> :
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <RateReviewRoundedIcon aria-hidden="true" sx={{ color: BLUE, fontSize: 22, mt: 0.25 }} />
                    <Box>
                        <Typography component="h3" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Feedback after answer {feedbackRound}</Typography>
                        <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: NAVY }}>{exercise.revisionFeedback[(feedbackRound - 1) % exercise.revisionFeedback.length]}</Typography>
                        <Typography sx={{ mt: 1.25, fontFamily: INTER, fontSize: 12, lineHeight: 1.5, color: SLATE }}>
                            {submitted ? 'This was sample feedback shown before you finished.' : 'This preview uses scripted sample feedback, not a review of your actual answer. You can change your answer for more feedback or finish now.'}
                        </Typography>
                    </Box>
                </Stack>}
            </Box>}

            {needsText && <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <EditOutlinedIcon aria-hidden="true" sx={{ fontSize: 19, color: BLUE }} />
                    <Typography component="label" htmlFor={`${exercise.id}-answer`} sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>Your written answer</Typography>
                </Stack>
                <TextField
                    id={`${exercise.id}-answer`}
                    fullWidth multiline minRows={desktop ? 5 : 4}
                    value={answer} disabled={submitted || recording}
                    onChange={event => changeAnswer(event.target.value)}
                    placeholder="在这里写你的回答……"
                    inputProps={{ lang: 'zh' }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '12px', fontFamily: INTER, fontSize: 16, color: NAVY, '& fieldset': { borderColor: RING_TRACK } } }}
                />
                {take && exercise.kind === 'openTextAudio' && <Typography sx={{ mt: 0.75, fontFamily: INTER, fontSize: 12, color: SLATE }}>
                    Editing this answer will clear the recording so your words and audio stay together.
                </Typography>}
            </Box>}

            {needsAudio && <Box id={`${exercise.id}-recording`} tabIndex={-1} sx={{ '&:focus-visible': { outline: `3px solid ${BLUE}`, outlineOffset: 4, borderRadius: '16px' } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <MicNoneRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: BLUE }} />
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>Your recording</Typography>
                </Stack>
                <Box sx={{ border: `1px solid ${RING_TRACK}`, borderRadius: '16px', p: 2, bgcolor: '#fff' }}>
                    {recording ? <Stack spacing={1.5} alignItems="flex-start">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', p: 1.5, borderRadius: '12px', bgcolor: `${RED}12` }}>
                            <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: RED, flexShrink: 0 }} />
                            <Typography role="status" sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: ERROR_TEXT, flex: 1 }}>Recording… speak, then tap Stop</Typography>
                            <Typography aria-live="off" aria-label={`Elapsed recording time ${elapsedLabel(recordingSeconds)}`} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: ERROR_TEXT }}>{elapsedLabel(recordingSeconds)}</Typography>
                        </Stack>
                        <Button fullWidth onClick={stopRecording} variant="contained" startIcon={<StopRoundedIcon />} sx={{ ...yellowButtonSx, bgcolor: ERROR_TEXT, color: '#fff', '&:hover': { bgcolor: ERROR_TEXT } }}>Stop and save</Button>
                    </Stack> : take ? <Stack spacing={1.5}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: NAVY }}>Take saved · {elapsedLabel(take.seconds)}</Typography>
                        <audio ref={playbackRef} src={take.url} onEnded={() => setPlaying(false)} aria-hidden="true" />
                        <Button fullWidth onClick={togglePlayback} startIcon={playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                            sx={{ minHeight: 44, border: `1px solid ${RING_TRACK}`, borderRadius: '999px', color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                            {playing ? 'Pause your recording' : 'Play your recording'}
                        </Button>
                        {!submitted && <Button onClick={startRecording} disabled={starting} startIcon={<MicNoneRoundedIcon />} sx={{ alignSelf: 'flex-start', minHeight: 44, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{starting ? 'Opening microphone…' : 'Record again'}</Button>}
                    </Stack> : <Stack spacing={1.25} alignItems="flex-start">
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE }}>Record, listen back, then submit when you are ready.</Typography>
                        <Button fullWidth onClick={startRecording} disabled={starting || submitted || (needsText && !answer.trim())} variant="contained" startIcon={<MicNoneRoundedIcon />} sx={yellowButtonSx}>{starting ? 'Opening microphone…' : 'Start recording'}</Button>
                        {needsText && !answer.trim() && <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Write your answer first.</Typography>}
                    </Stack>}
                </Box>
                {error && <Typography role="alert" sx={{ mt: 1, fontFamily: INTER, fontSize: 13, color: '#A82735' }}>{error}</Typography>}
            </Box>}

            {submitted ? <Box role="status" sx={{ p: 2, borderRadius: '14px', bgcolor: `${GREEN}14`, border: `1px solid ${GREEN}40` }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleOutlineRoundedIcon sx={{ color: GREEN, fontSize: 22 }} />
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>{exercise.revisionFeedback ? 'Final answer submitted in this preview' : 'Response submitted in this preview'}</Typography>
                        <Typography sx={{ mt: 0.5, fontFamily: INTER, fontSize: 13, color: SLATE }}>
                            {exercise.revisionFeedback
                                ? 'You can continue. This preview does not send your answer to a class or assign a grade.'
                                : needsAudio
                                ? 'You can continue. In a live assignment, a teacher would review your final answer before releasing a grade.'
                                : 'You can continue. This writing preview does not assign a grade.'}
                        </Typography>
                    </Box>
                </Stack>
            </Box> : <Box>
                {exercise.revisionFeedback && <Button fullWidth variant="contained" disabled={!canSubmit || starting || recording} onClick={finishAnswer} sx={blueButtonSx}>Finish and submit</Button>}
                <Button fullWidth variant={exercise.revisionFeedback ? 'outlined' : 'contained'} disabled={!canSubmit || starting || recording || !hasRevision} onClick={submitAnswer}
                    sx={exercise.revisionFeedback ? { mt: 1.25, minHeight: 44, borderColor: RING_TRACK, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none', borderRadius: '999px' } : blueButtonSx}>
                    {exercise.revisionFeedback ? 'Get sample feedback' : 'Submit answer'}
                </Button>
                {exercise.revisionFeedback && <Typography sx={{ mt: 1, fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center' }}>You can finish now. Sample feedback is optional in this preview; live AI feedback may take about a minute.</Typography>}
                <Typography sx={{ mt: 1, fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center' }}>
                    Preview only · your answer is not sent or saved to a class.
                </Typography>
            </Box>}
        </Stack>
    </Box>;
}
